"use client";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { TopSheen } from "./TopSheen";

// The design shows only "Up to HK$15,000"; the remaining bands aren't
// specified, so these are a sensible progression.
export const EXPENSE_BANDS = [
  "Up to HK$15,000",
  "HK$15,001 – HK$50,000",
  "HK$50,001 – HK$100,000",
  "Over HK$100,000",
];

/**
 * Monthly-expenses band picker. The trigger is two lines (band + caption), so
 * the value is rendered directly rather than through SelectValue, which only
 * handles a single text node.
 *
 * With an empty `value` the trigger shows `placeholder` on one line and drops
 * the caption — the unchosen state the manage branch's accounting screen starts
 * in. The register branch arrives with a band already set and never sees it.
 */
export function MonthlyExpensesSelect({
  value,
  onChange,
  placeholder = "Select average monthly expenses",
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="relative w-full">
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          aria-label="Monthly expenses"
          className="h-16 w-full cursor-pointer rounded-lg border-0 bg-[#0F1319] px-6 text-left ring-offset-0 focus:ring-0 focus:ring-offset-0 [&>svg]:h-6 [&>svg]:w-6 [&>svg]:stroke-[2.5] [&>svg]:opacity-100"
        >
          {/* A <div>, not a <span>: SelectTrigger's own className has
              `[&>span]:line-clamp-1`, meant for SelectValue's plain-text span.
              That rule sets `display:-webkit-box`, which silently overrides
              flex-col on any direct <span> child — including this one — so
              the two lines below would render side by side instead of
              stacked. A <div> doesn't match `> span` and is unaffected. */}
          <div className="flex flex-col items-start gap-1">
            <span className="font-suisse-intl-medium text-base text-white">
              {value || placeholder}
            </span>
            {value && (
              <span className="font-inter text-sm text-[#9CA3AF]">monthly expenses</span>
            )}
          </div>
        </SelectTrigger>
        <SelectContent
          position="popper"
          side="bottom"
          avoidCollisions={false}
          sideOffset={8}
          className="max-h-[320px] rounded-lg border-0 bg-[#0F1319] p-1 text-white shadow-[0px_8px_24px_rgba(0,0,0,0.45)] outline outline-1 -outline-offset-1 outline-white/10 [&_[data-radix-select-viewport]]:h-auto"
        >
          {EXPENSE_BANDS.map((band) => (
            <SelectItem
              key={band}
              value={band}
              className="cursor-pointer rounded-md py-3 pr-3 font-suisse-intl-medium text-base text-white focus:bg-white/10 focus:text-white data-[state=checked]:bg-white/[0.06]"
            >
              {band}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <TopSheen />
    </div>
  );
}
